import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Row,
  Skeleton,
  Tag,
  Typography,
  message,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { postReq, downloadReq } from "../api.js";
import dayjs from "dayjs";
import { currencyFormat } from "../utils/currencyFormat.js";
const { Text, Title } = Typography;

const userDetails = JSON.parse(localStorage.getItem("userDetails"));

// ─── helpers ─────────────────────────────────────────────────────────────────

const pct = (v) => `${v}%`;

const statusColors = {
  GENERATED: { color: "green", label: "Generated" },
  PENDING: { color: "gold", label: "Pending" },
  PAID: { color: "blue", label: "Paid" },
};

// ─── InfoRow ─────────────────────────────────────────────────────────────────

const InfoRow = ({ label, value, bold, accent }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "5px 0",
    }}
  >
    <Text type="secondary" style={{ fontSize: 13 }}>
      {label}
    </Text>
    <Text
      style={{
        fontWeight: bold ? 700 : 500,
        fontSize: bold ? 14 : 13,
        color: accent ? "#1976D2" : undefined,
      }}
    >
      {value}
    </Text>
  </div>
);

// ─── SectionCard ─────────────────────────────────────────────────────────────

const SectionCard = ({ title, accentColor = "#1976D2", children }) => (
  <Card
    bordered
    styles={{
      header: {
        background: `linear-gradient(90deg, ${accentColor}18 0%, transparent 100%)`,
        borderBottom: "1px solid #f0f0f0",
        minHeight: 40,
        padding: "0 16px",
      },
      body: { padding: "12px 16px" },
    }}
    title={
      <Text
        style={{
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: 1,
          textTransform: "uppercase",
          color: accentColor,
        }}
      >
        {title}
      </Text>
    }
    style={{ borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
  >
    {children}
  </Card>
);

// ─── StatPill ────────────────────────────────────────────────────────────────

const StatPill = ({ label, value, color }) => (
  <div
    style={{
      flex: "1 1 90px",
      minWidth: 80,
      textAlign: "center",
      border: `1px solid ${color}40`,
      borderRadius: 8,
      padding: "8px 10px",
      background: `${color}0D`,
    }}
  >
    <div style={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1 }}>
      {value}
    </div>
    <div
      style={{ fontSize: 11, color: "#888", marginTop: 4, letterSpacing: 0.3 }}
    >
      {label}
    </div>
  </div>
);

// ─── LoadingSkeleton ──────────────────────────────────────────────────────────

const LoadingSkeleton = () => (
  <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
    {[...Array(6)].map((_, i) => (
      <Col key={i} xs={24} sm={12} lg={8}>
        <Card bordered style={{ borderRadius: 8 }}>
          <Skeleton active paragraph={{ rows: 4 }} />
        </Card>
      </Col>
    ))}
  </Row>
);

// ─── EmptyState ───────────────────────────────────────────────────────────────

const EmptyState = ({ month }) => (
  <div
    style={{
      marginTop: 32,
      padding: "48px 0",
      textAlign: "center",
      border: "1px dashed #d9d9d9",
      borderRadius: 8,
    }}
  >
    <div style={{ fontSize: 32 }}>📭</div>
    <Title level={5} style={{ marginTop: 8 }}>
      No payroll record found
    </Title>
    <Text type="secondary">
      No salary record generated for {dayjs(month).format("MMMM YYYY")}
    </Text>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const PayrollRecords = ({ state }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());

  const fetchData = async () => {
    setLoading(true);
    setData(null);
    try {
      const res = await postReq("user/list_salaryrecord_user", {
        userId: userDetails._id,
        month: selectedMonth.month() + 1,
        year: selectedMonth.year(),
      });
      if (res.status === 200) {
        setData(res.data);
      } else {
        message.error(res.message || "Error while fetching salary record");
      }
    } catch (error) {
      message.error(error.message || "Error while fetching salary record");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const month = selectedMonth.month() + 1;
      const year = selectedMonth.year();
      const res = await downloadReq("user/download_salaryslip", {
        month,
        year,
        userId: userDetails._id,
      });

      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );
      const monthName = new Date(year, month - 1).toLocaleString("default", {
        month: "short",
      });
      const userName = `${userDetails.name}`;
      const fileName = `SalarySlip_${userName}_${monthName}-${year}.pdf`;

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      message.error("Failed to download salary slip");
    } finally {
      setDownloading(false);
    }
  };

  // ─── derived values ──────────────────────────────────────────────────────

  const snap = data?.salarySnapshot ?? {};
  const att = data?.attendanceSnapshot ?? {};
  const policy = data?.policySnapshot ?? {};
  const earnings = data?.earnings ?? {};
  const deductions = data?.deductions ?? {};

  const totalDeductions =
    (deductions.professionalTax ?? 0) +
    (deductions.pfEmployee ?? 0) +
    (deductions.esicEmployee ?? 0) +
    (deductions.pfEmployer ?? 0) +
    (deductions.esicEmployer ?? 0);

  const statusMeta = statusColors[data?.status] ?? statusColors.GENERATED;

  const attendancePct = att.totalWorkingDays
    ? Math.round((att.payableDays / att.totalWorkingDays) * 100)
    : 0;

  return (
    <div style={{ width: "100%" }}>
      {/* ── Toolbar ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          padding: "16px 0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <DatePicker
            picker="month"
            value={selectedMonth}
            disabledDate={(d) => d && d.isAfter(dayjs(), "month")}
            onChange={(val) => val && setSelectedMonth(val)}
            format="MMMM YYYY"
            allowClear={false}
            style={{ width: 200 }}
          />

          {data && !loading && (
            <Tag
              color={statusMeta.color}
              style={{ fontWeight: 700, fontSize: 12, borderRadius: 4 }}
            >
              {statusMeta.label}
            </Tag>
          )}
        </div>

        <Button
          icon={<DownloadOutlined />}
          loading={downloading}
          onClick={downloadPdf}
        >
          Download
        </Button>
      </div>

      {/* ── Loading ── */}
      {loading && <LoadingSkeleton />}

      {/* ── Empty ── */}
      {!loading && !data && <EmptyState month={selectedMonth} />}

      {/* ── Cards Grid ── */}
      {!loading && data && (
        <Row gutter={[16, 16]} style={{ paddingBottom: 24 }}>
          {/* 1 — Salary Snapshot */}
          <Col xs={24} sm={12} lg={8}>
            <SectionCard title="Salary Snapshot" accentColor="#1976D2">
              <InfoRow
                label="Monthly CTC"
                value={currencyFormat(snap.monthlyCTC ?? 0)}
                bold
              />
              <InfoRow
                label="Attendance Required"
                value={snap.isAttendanceRequired ? "Yes" : "No"}
              />
            </SectionCard>
          </Col>

          {/* 2 — Attendance */}
          <Col xs={24} sm={12} lg={8}>
            <SectionCard title="Attendance" accentColor="#0288D1">
              {/* Progress bar */}
              <div style={{ marginBottom: 12 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 4,
                  }}
                >
                  <Text style={{ fontSize: 11, color: "#888" }}>
                    Payable days
                  </Text>
                  <Text
                    style={{ fontSize: 11, fontWeight: 700, color: "#0288D1" }}
                  >
                    {att.payableDays} / {att.totalWorkingDays} days (
                    {attendancePct}%)
                  </Text>
                </div>
                <div
                  style={{
                    height: 6,
                    background: "#E3F2FD",
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${attendancePct}%`,
                      background: "#0288D1",
                      borderRadius: 3,
                      transition: "width 0.6s ease",
                    }}
                  />
                </div>
              </div>

              {/* Stat pills */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <StatPill
                  label="Present"
                  value={att.presentDays ?? 0}
                  color="#2E7D32"
                />
                <StatPill
                  label="Half Days"
                  value={att.halfDays ?? 0}
                  color="#F57F17"
                />
                <StatPill
                  label="Paid Leave"
                  value={att.paidLeaveDays ?? 0}
                  color="#1976D2"
                />
                <StatPill
                  label="Unpaid Leave"
                  value={att.unpaidLeaveDays ?? 0}
                  color="#C62828"
                />
              </div>
            </SectionCard>
          </Col>

          {/* 3 — Earnings */}
          <Col xs={24} sm={12} lg={8}>
            <SectionCard title="Earnings" accentColor="#388E3C">
              <InfoRow
                label="Basic"
                value={currencyFormat(earnings.basic ?? 0)}
              />
              <InfoRow label="HRA" value={currencyFormat(earnings.hra ?? 0)} />
              <InfoRow
                label="Special Allowance"
                value={currencyFormat(earnings.specialAllowance ?? 0)}
              />
              <Divider style={{ margin: "8px 0" }} />
              <InfoRow
                label="Gross Payable"
                value={currencyFormat(data.grossPayable ?? 0)}
                bold
                accent
              />
            </SectionCard>
          </Col>

          {/* 4 — Deductions */}
          <Col xs={24} sm={12} lg={8}>
            <SectionCard title="Deductions" accentColor="#D32F2F">
              <InfoRow
                label="Professional Tax"
                value={currencyFormat(deductions.professionalTax ?? 0)}
              />
              <InfoRow
                label="PF (Employee)"
                value={currencyFormat(deductions.pfEmployee ?? 0)}
              />
              <InfoRow
                label="PF (Employer)"
                value={currencyFormat(deductions.pfEmployer ?? 0)}
              />
              {policy.esicEnabled && (
                <InfoRow
                  label="ESIC (Employer)"
                  value={currencyFormat(deductions.esicEmployer ?? 0)}
                />
              )}
              {policy.esicEnabled && (
                <InfoRow
                  label="ESIC (Employee)"
                  value={currencyFormat(deductions.esicEmployee ?? 0)}
                />
              )}
              <Divider style={{ margin: "8px 0" }} />
              <InfoRow
                label="Total Deductions"
                value={currencyFormat(totalDeductions)}
                bold
              />
            </SectionCard>
          </Col>

          {/* 5 — Policy Config */}
          <Col xs={24} sm={12} lg={8}>
            <SectionCard title="Policy Config" accentColor="#546E7A">
              <InfoRow label="Basic %" value={pct(policy.basicPercent ?? 0)} />
              <InfoRow label="HRA %" value={pct(policy.hraPercent ?? 0)} />
              <InfoRow
                label="Special Allowance %"
                value={pct(policy.specialAllowancePercent ?? 0)}
              />
              <InfoRow
                label="PF Enabled"
                value={policy.pfEnabled ? "Yes" : "No"}
              />
              <InfoRow
                label="ESIC Enabled"
                value={policy.esicEnabled ? "Yes" : "No"}
              />
              <InfoRow
                label="PF Employee %"
                value={pct(policy.pfEmployeePercent ?? 0)}
              />
              <InfoRow
                label="PF Employer %"
                value={pct(policy.pfEmployerPercent ?? 0)}
              />
              <InfoRow
                label="PF Max Salary"
                value={currencyFormat(policy.pfMaxEligibleSalary ?? 0)}
              />
            </SectionCard>
          </Col>
        </Row>
      )}

      {/* ── Net Payable Banner ── */}
      {!loading && data && (
        <div
          style={{
            marginBottom: 24,
            padding: "20px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
            borderRadius: 8,
            background: "linear-gradient(135deg, #1976D2 0%, #1565C0 100%)",
            boxShadow: "0 4px 16px rgba(25,118,210,0.3)",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                color: "rgba(255,255,255,0.8)",
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            >
              Net Payable for {selectedMonth.format("MMMM YYYY")}
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: 800,
                color: "#fff",
                lineHeight: 1.2,
                marginTop: 4,
              }}
            >
              {currencyFormat(data.netPayable ?? 0)}
            </div>
          </div>

          <div style={{ textAlign: "right" }}>
            <div
              style={{
                fontSize: 11,
                color: "rgba(255,255,255,0.75)",
                marginBottom: 4,
              }}
            >
              Gross ─ Deductions
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#fff" }}>
              {currencyFormat(data.grossPayable ?? 0)} −{" "}
              {currencyFormat(totalDeductions)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollRecords;
